import { useDispatch, useSelector } from "react-redux";
import { Colors } from "../../../../utils/colors";
import RectangleButton from "../../../Buttons/RectangleButton";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import CustomTextField from "../../../CustomTextField";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import {
  accountActions,
  getAccountState,
} from "../../../../redux/slices/accountSlice";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { showPhoneInputError } from "../../../../utils/phoneUtils";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import {
  AccountData,
  CustomTicketV2,
  Event,
  FollowSourceType,
  FollowType,
  FormAnswersV2,
  NotificationType,
  SavedFormQuestion,
} from "@markit/common.types";
import { MixpanelContext } from "../../../../context/AnalyticsService";
import { followUser } from "../../../../utils/FollowUser";
import CustomPhoneTextField from "../../../TextFields/CustomPhoneTextField";
import {
  getTicketsRef,
  getUserData,
  getUserFollowersRef,
  getUserRef,
} from "../../../../utils/FirebaseUtils";
import {
  limit,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "../../../../firebase";
import {
  createEmptyFormAnswersV2,
  isEventRelatedType,
  isPaidTicketType,
  isProductSpecificType,
  MARKIT_TERMS_OF_USE,
} from "@markit/common.utils";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import { API } from "../../../../API";
import SingularQuestionInput from "../../../UserInfoContainers/SingularQuestionInput";
import {
  finalizeFormFileUploadAnswers,
  getEventCustomTicketFormQuestions,
  getEventFormQuestions,
  getUsersFormResponses,
  validateEventForm,
} from "../../../../utils/eventUtils/formUtils";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import SearchBoxContactsDropdown from "../../../Containers/SearchBox/SearchBoxContactsDropdown";
import { HorizontalDividerWithText } from "../../../Dividers/HorizontalDividerWithText";
import { FormResponsesProvider } from "../../../Form/FormResponsesContext";
import DropdownArrowIcon from "../../../DropdownsAndTabs/DropdownArrowIcon";

export type AddSingleContactModalProps = {
  setIsVisible: (isVisible: boolean) => void;
  event?: Event; // for adding single guest
  existingContact?: AccountData;
  setFetchedUserData?: React.Dispatch<React.SetStateAction<AccountData[]>>;
};

const AddSingleContactModal = (props: AddSingleContactModalProps) => {
  const { setIsVisible, event, existingContact, setFetchedUserData } = props;
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);
  const [fullName, setFullName] = useState(
    existingContact ? existingContact.fullName : ""
  );
  const [phoneNumber, setPhoneNumber] = useState(
    existingContact ? existingContact.phoneNumber : ""
  );
  const [followerUid, setFollowerUid] = useState(
    existingContact ? existingContact.uid : ""
  );
  const [fullNameError, setFullNameError] = useState({
    isError: false,
    message: "",
  });
  const [phoneNumberError, setPhoneNumberError] = useState({
    isError: false,
    message: "",
  });
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [numTicketsDropdownVisible, setNumTicketsDropdownVisible] =
    useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [eventFormQuestions, setEventFormQuestions] = useState<
    SavedFormQuestion[]
  >([]);
  const [selectedCustomTicket, setSelectedCustomTicket] =
    useState<CustomTicketV2>();
  const [numTickets, setNumTickets] = useState(0);
  const [answers, setAnswers] = useState<FormAnswersV2>(
    createEmptyFormAnswersV2([], [])
  );
  const [formFileUploads, setFormFileUploads] = useState<
    Record<string, string>
  >({});
  const [formErrorIds, setFormErrorIds] = useState<string[]>([]);

  const styles = {
    dropdownContainer: {
      padding: "10px 14px",
      borderRadius: 12,
      border: `1px solid ${Colors.GRAY2}`,
    },
  };

  const isEditing = useMemo(() => !!existingContact, [existingContact]);

  const madeChanges = useMemo(
    () =>
      !isEditing ||
      (isEditing &&
        (fullName.trim() !== existingContact?.fullName ||
          phoneNumber.trim() !== existingContact?.phoneNumber)),
    [isEditing, fullName, phoneNumber, existingContact]
  );

  const singleContactTerms = useMemo(() => {
    if (!event) return undefined;
    if (isEventRelatedType(event.eventType)) {
      // Event related type
      return {
        type: "Event",
        title: "Guest",
        item: "Ticket Type",
      };
    } else if (isProductSpecificType(event.eventType)) {
      // Product specific type
      return {
        type: "Product",
        title: "Order",
        item: "Order Option",
      };
    } else {
      // Form Submission type
      return {
        type: "Form",
        title: "Submission",
        item: "",
      };
    }
  }, [event]);

  const formQuestionsToDisplay = useMemo(
    () =>
      selectedCustomTicket
        ? getEventCustomTicketFormQuestions(
            [selectedCustomTicket],
            eventFormQuestions
          )
        : [],
    [selectedCustomTicket, eventFormQuestions]
  );

  useAsyncOnMount(async () => {
    if (event && event.customTickets.length > 0) {
      // Default to the first custom ticket
      setSelectedCustomTicket(event.customTickets[0]);
      setNumTickets(event.customTickets[0].minQuantity ?? 1);
      const formQuestions = await getEventFormQuestions(event);
      setEventFormQuestions(formQuestions);
      setAnswers(createEmptyFormAnswersV2(formQuestions, []));
    }
  });

  const searchContactOnPress = useCallback(
    async (contact: AccountData) => {
      setFullName(contact.fullName);
      setPhoneNumber(contact.phoneNumber);
      setFollowerUid(contact.uid);
      setSearchTerm("");
      if (event) {
        // Clear answers in case there was a previous contact selected
        setAnswers(createEmptyFormAnswersV2(eventFormQuestions, []));
        const { formResponses, fileNameRecords } = await getUsersFormResponses(
          event.createdBy,
          eventFormQuestions,
          contact.uid
        );

        setAnswers(createEmptyFormAnswersV2(eventFormQuestions, formResponses));
        setFormFileUploads(fileNameRecords);

        const hasResponses = formResponses.some((formResponse) =>
          formResponse.responses.some((response) => response !== "")
        );
        if (hasResponses) {
          showNotificationBanner(
            dispatch,
            "Autofilled Responses",
            NotificationType.AFFIRMATIVE
          );
        }
      }
    },
    [dispatch, event, eventFormQuestions]
  );

  const addSingleContact = useCallback(async () => {
    const success = await followUser({
      userPhone: phoneNumber,
      userFullName: fullName.trim(),
      userToFollow: accountData,
      uid: "",
      answers: undefined,
      followType: FollowType.UPLOAD,
      disableTriggerMessage: true,
      manualAddContact: true,
    });
    if (!success) {
      setProcessing(false);
      return;
    }
    // Add live snapshot to populate redux once follower data is created
    const followersRef = getUserFollowersRef(accountData.uid);
    const followersQuery = query(
      followersRef,
      where("phoneNumber", "==", phoneNumber),
      limit(1)
    );
    const unsubscribe = onSnapshot(followersQuery, async (snapshot) => {
      const followerData = snapshot.docs[0].data();
      if (followerData) {
        const userData = await getUserData(followerData.uid);
        if (userData) {
          dispatch(accountActions.addMultipleFollowingAccountData([userData]));
          dispatch(accountActions.addMultipleFollowerData([followerData]));
        } else {
          alert("Please refresh the page to view the new contact. Thank you.");
        }
        if (mixpanel) {
          mixpanel.track("Added Single Follower Contact", {
            distinct_id: accountData.uid,
            phone_number: phoneNumber,
            full_name: fullName,
          });
        }
        setIsVisible(false);
        setProcessing(false);
        showNotificationBanner(
          dispatch,
          "Added Contact",
          NotificationType.AFFIRMATIVE
        );
        unsubscribe();
      }
    });
  }, [phoneNumber, fullName, accountData, mixpanel, setIsVisible, dispatch]);

  const addSingleGuest = useCallback(async () => {
    if (event && selectedCustomTicket) {
      const errorIds = validateEventForm(answers, formQuestionsToDisplay);
      if (errorIds.length > 0) {
        setFormErrorIds(errorIds);
        alert("Please fill in the required questions.");
        setProcessing(false);
        return;
      }

      // Clear answers that are not on the selected custom ticket so we only submit the answers that are on the selected custom ticket
      const filteredAnswers = Object.keys(answers).reduce(
        (acc: FormAnswersV2, questionId: string) => {
          if (
            formQuestionsToDisplay.find(
              (formQuestion) => formQuestion.id === questionId
            )
          ) {
            acc[questionId] = answers[questionId];
          }
          return acc;
        },
        createEmptyFormAnswersV2(formQuestionsToDisplay, [])
      );

      let currentAnswers = filteredAnswers;
      try {
        currentAnswers = await finalizeFormFileUploadAnswers({
          creatorUid: event.createdBy,
          answers: currentAnswers,
          formQuestions: formQuestionsToDisplay,
          fileNamesByQuestionId: formFileUploads,
          eventId: event.id,
          setAnswers,
          followerUid: followerUid,
        });
      } catch (e: any) {
        setProcessing(false);
        alert("Failed to upload a file. Please try again.");
        return;
      }

      // If the selected custom ticket is a paid option, we generate a free ticket first and then pass that into redeemTicketId
      let redeemTicketIds: string[] = [];
      if (isPaidTicketType(selectedCustomTicket)) {
        try {
          await API.promoCodes
            .createFreeTicketCode({
              eventId: event.id,
              uid: event.createdBy,
              customTicketId: selectedCustomTicket.id,
              alias: "",
              maxUseCount: numTickets ?? 1,
            })
            .then((response) => {
              mixpanel.track("Generated Free Ticket Code", {
                event_id: event.id,
                ticket_id: response.freeTicketCodes[0].id,
                type: event.eventType,
                source: "add single guest",
              });
              redeemTicketIds = response.freeTicketCodes.map(
                (freeTicketCode) => freeTicketCode.id
              );
            });
        } catch (e) {
          alert("An error occurred when adding a guest. Please try again.");
          setProcessing(false);
          return;
        }
      }

      await API.rsvp
        .enqueueRSVPToEvent({
          toPhoneNumber: phoneNumber,
          fullName: fullName.trim(),
          eventId: event.id,
          followSourceType: FollowSourceType.DESKTOP_WEB,
          answers:
            formQuestionsToDisplay.length !== 0 ? currentAnswers : undefined,
          customTicketIds: [selectedCustomTicket.id],
          numberOfTicketsByCustomTicketId: {
            [selectedCustomTicket.id]: numTickets ?? 1,
          },
          redeemTicketIds: redeemTicketIds,
          promoCodeId: "",
          promoterId: "",
          linkId: "",
          userReferrer: "",
          amount: 0,
          chargeId: "",
          customerId: "",
          uid: "",
        })
        .then(async (response) => {
          const { success, firstTicketIdTracker } = response;
          if (!success) {
            alert("An error occurred when adding a guest. Please try again.");
          } else {
            const ticketsRef = getTicketsRef(event.id);
            const ticketIdTrackerQuery = query(
              ticketsRef,
              where("id", "==", firstTicketIdTracker)
            );

            const unsubscribe = onSnapshot(ticketIdTrackerQuery, (snapshot) => {
              snapshot.docChanges().forEach(async (change) => {
                const newTicket = change.doc.data();
                if (newTicket) {
                  if (mixpanel) {
                    mixpanel.track("Added Single Follower Contact", {
                      distinct_id: accountData.uid,
                      phone_number: phoneNumber,
                      full_name: fullName,
                      event_id: event.id,
                    });
                  }
                  setIsVisible(false);
                  setProcessing(false);
                  showNotificationBanner(
                    dispatch,
                    `Added ${singleContactTerms?.title}`,
                    NotificationType.AFFIRMATIVE
                  );
                  unsubscribe();
                }
              });
            });
          }
        });
    }
  }, [
    event,
    selectedCustomTicket,
    answers,
    formQuestionsToDisplay,
    phoneNumber,
    fullName,
    numTickets,
    formFileUploads,
    followerUid,
    mixpanel,
    setIsVisible,
    dispatch,
    singleContactTerms?.title,
    accountData.uid,
  ]);

  // TODO: Currently updates only the full name on the accountData document.
  // We'll want to update the follower data instead once we add the full name there.
  const editSingleContact = useCallback(async () => {
    if (!existingContact) {
      setProcessing(false);
      return;
    }
    const fullNameTrimmed = fullName.trim();

    try {
      const userRef = getUserRef(existingContact.uid);
      await updateDoc(userRef, { fullName: fullNameTrimmed });
      dispatch(
        accountActions.addMultipleFollowingAccountData([
          { ...existingContact, fullName: fullNameTrimmed },
        ])
      );

      // This is temporary until we add and update the fullName on follower data, which would trigger the live update on hook
      if (setFetchedUserData) {
        setFetchedUserData((prevFetchedUserData: AccountData[]) => [
          ...prevFetchedUserData.map((fetchedUserData) =>
            fetchedUserData.uid === existingContact.uid
              ? { ...fetchedUserData, fullName: fullNameTrimmed }
              : fetchedUserData
          ),
        ]);
      }

      if (mixpanel) {
        mixpanel.track("Added Single Follower Contact", {
          distinct_id: accountData.uid,
          phone_number: phoneNumber,
          full_name: fullName,
          edited_uid: existingContact.uid,
          is_editing: true,
        });
      }
      showNotificationBanner(
        dispatch,
        "Contact Updated",
        NotificationType.AFFIRMATIVE
      );
      setIsVisible(false);
      setProcessing(false);
    } catch (e) {
      showNotificationBanner(
        dispatch,
        "Update Failed",
        NotificationType.NEGATIVE
      );
      setProcessing(false);
    }
  }, [
    accountData.uid,
    dispatch,
    existingContact,
    fullName,
    mixpanel,
    phoneNumber,
    setFetchedUserData,
    setIsVisible,
  ]);

  const handleAddContact = useCallback(async () => {
    if (fullName.trim() === "") {
      setFullNameError({
        isError: true,
        message: "Full name cannot be empty.",
      });
      return;
    }
    if (showPhoneInputError(phoneNumber, setPhoneNumberError)) {
      return;
    }
    setProcessing(true);

    if (isEditing) {
      await editSingleContact();
    } else {
      if (event) {
        await addSingleGuest();
      } else {
        await addSingleContact();
      }
    }
  }, [
    addSingleContact,
    addSingleGuest,
    editSingleContact,
    event,
    fullName,
    isEditing,
    phoneNumber,
  ]);

  const ticketDropdownItems: DropdownMenuItem[] = useMemo(() => {
    return event
      ? event.customTickets.map((ticket) => ({
          title: ticket.label + `${ticket.hideTicket ? " (Hidden)" : ""}`,
          icon:
            selectedCustomTicket?.id === ticket.id
              ? "ion:checkmark-circle"
              : undefined,
          key: ticket.id,
          onPress: () => {
            setSelectedCustomTicket(ticket);
            setNumTickets((current) => {
              const min = ticket.minQuantity ?? 1;
              const max = ticket.maxQuantity ?? 1;
              return current >= min && current <= max ? current : min;
            });
          },
        }))
      : [];
  }, [event, selectedCustomTicket?.id]);

  const numTicketsDropdownItems: DropdownMenuItem[] = useMemo(() => {
    if (!selectedCustomTicket) return [];
    const min = selectedCustomTicket.minQuantity ?? 1;
    const max = selectedCustomTicket.maxQuantity ?? 1;
    const count = Math.max(0, max - min + 1);
    return Array.from({ length: count }, (_, index) => {
      const quantity = min + index;
      return {
        title: quantity.toString(),
        icon: numTickets === quantity ? "ion:checkmark-circle" : undefined,
        key: quantity.toString(),
        onPress: () => setNumTickets(quantity),
      };
    });
  }, [selectedCustomTicket, numTickets]);

  const renderEventSpecificContent = useMemo(
    () => (
      <div className="ColumnNormal" style={{ gap: 14 }}>
        {singleContactTerms?.type !== "Form" ? (
          <>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodyMedium">
                Select {singleContactTerms?.item}
              </span>
              <DropdownMenu
                dropdownItems={ticketDropdownItems}
                trigger={["click"]}
                placement="bottom"
                onOpenChange={(isOpen: boolean) => setDropdownVisible(isOpen)}
              >
                <div
                  className="AlignedRowSpaced"
                  style={styles.dropdownContainer}
                >
                  <span className="bodyMedium">
                    {selectedCustomTicket?.label || "Select an option"}
                  </span>
                  <DropdownArrowIcon
                    isOpen={dropdownVisible}
                    color={Colors.BLACK}
                    height={14}
                  />
                </div>
              </DropdownMenu>
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodyMedium">
                Number of{" "}
                {event && isEventRelatedType(event.eventType)
                  ? "Tickets"
                  : "Orders"}
              </span>
              <DropdownMenu
                dropdownItems={numTicketsDropdownItems}
                trigger={["click"]}
                placement="bottom"
                onOpenChange={setNumTicketsDropdownVisible}
              >
                <div
                  className="AlignedRowSpaced"
                  style={styles.dropdownContainer}
                >
                  <span className="bodyMedium">{numTickets}</span>
                  <DropdownArrowIcon
                    isOpen={numTicketsDropdownVisible}
                    color={Colors.BLACK}
                    height={14}
                  />
                </div>
              </DropdownMenu>
            </div>
          </>
        ) : null}
        {formQuestionsToDisplay.length > 0 ? (
          <>
            <HorizontalDivider altMargin={0} />
            <span className="bodyMedium">Enter Responses</span>
            <FormResponsesProvider
              formFileUploads={formFileUploads}
              setFormFileUploads={setFormFileUploads}
              formErrorIds={formErrorIds}
              setFormErrorIds={setFormErrorIds}
            >
              <div className="ColumnNormal" style={{ gap: 14 }}>
                {formQuestionsToDisplay.map((question) => {
                  return (
                    <SingularQuestionInput
                      key={question.id}
                      question={question}
                      formQuestions={formQuestionsToDisplay}
                      answers={answers}
                      setAnswers={setAnswers}
                      disabled={false}
                    />
                  );
                })}
              </div>
            </FormResponsesProvider>
            <HorizontalDivider altMargin={0} />
          </>
        ) : null}
      </div>
    ),
    [
      singleContactTerms?.type,
      singleContactTerms?.item,
      ticketDropdownItems,
      styles.dropdownContainer,
      selectedCustomTicket?.label,
      dropdownVisible,
      event,
      numTicketsDropdownItems,
      numTickets,
      numTicketsDropdownVisible,
      formQuestionsToDisplay,
      formFileUploads,
      formErrorIds,
      answers,
    ]
  );

  return (
    <>
      <PopupModalContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <Icon
              icon={isEditing ? "ion:create" : "ion:person-add"}
              height={35}
              style={{ color: Colors.BLACK }}
            />
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="sectionTitle">
                {isEditing
                  ? "Edit Contact"
                  : `Add Single ${
                      singleContactTerms ? singleContactTerms.title : "Contact"
                    }`}
              </span>
              <span className="smallBodySubtext" style={{ fontWeight: 400 }}>
                {isEditing
                  ? "Manually update this contact's details."
                  : event
                    ? `Manually add someone to this ${singleContactTerms?.type.toLowerCase()}.`
                    : "Manually add a single contact to your audience."}
              </span>
              {event ? (
                <span className="smallBodySubtext" style={{ fontWeight: 400 }}>
                  Note: The contact will receive a confirmation text upon
                  adding.
                </span>
              ) : null}
            </div>
          </div>
        }
        valueComp={
          <div className="ColumnNormal" style={{ gap: 20, maxHeight: "60vh" }}>
            {event ? (
              <>
                <div className="ColumnNormal" style={{ gap: 7 }}>
                  <span className="bodyMedium">Search Contacts</span>
                  <SearchBoxContactsDropdown
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    contactOnPress={searchContactOnPress}
                  />
                </div>
                <HorizontalDividerWithText text="or enter manually" />
              </>
            ) : null}
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span className="bodyMedium">Full Name</span>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <CustomTextField
                    value={fullName}
                    placeholder="Full name"
                    inputMode="text"
                    borderRadius={12}
                    maxLength={60}
                    backgroundColor={Colors.WHITE}
                    error={fullNameError.isError}
                    onChange={(change: any) => {
                      setFullName(change.target.value);
                      setFullNameError({
                        isError: false,
                        message: "",
                      });
                    }}
                    altMarginBottom={0}
                  />
                  {fullNameError.isError ? (
                    <span style={{ color: Colors.RED3, fontSize: 12 }}>
                      {fullNameError.message}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span className="bodyMedium">Phone Number</span>
                <CustomPhoneTextField
                  placeholder="Phone number"
                  phoneNumber={phoneNumber}
                  onPhoneChange={(value) => {
                    setPhoneNumber(value);
                    setPhoneNumberError({ isError: false, message: "" });
                  }}
                  showPhoneError={phoneNumberError}
                  disabled={isEditing}
                />
              </div>
              {event ? renderEventSpecificContent : null}
            </div>
            <span className="smallBodySubtext">
              By pressing &quot;{isEditing ? "Save" : "Add"}&quot;, you
              acknowledge and agree you have obtained explicit consent from the
              individual whose phone number you are uploading, or will upload,
              and also agree to the following{" "}
              <span
                onClick={() => window.open(MARKIT_TERMS_OF_USE)}
                style={{
                  color: Colors.BLACK,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                terms.
              </span>
            </span>
          </div>
        }
        fixedBottomContent={
          <div className="ColumnNormal">
            <HorizontalDivider altMargin={14} />
            <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
              <RectangleButton
                buttonLabel={"Cancel"}
                onPress={() => setIsVisible(false)}
                altTextColor={Colors.BLACK}
                altColor={Colors.WHITE1}
                containerStyles={{
                  paddingBlock: 14,
                  paddingInline: 12,
                  border: `0.5px solid ${Colors.GRAY2}`,
                }}
              />
              <RectangleButton
                buttonLabel={isEditing ? "Save" : "Add"}
                onPress={handleAddContact}
                altTextColor={Colors.WHITE}
                altColor={Colors.BLACK}
                containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
                loading={processing}
                disabled={(event && !selectedCustomTicket) || !madeChanges}
              />
            </div>
          </div>
        }
        noExit
        closeModal={() => setIsVisible(false)}
        modalContentWidth={PopupModalContainerWidth.SMALL}
      />
    </>
  );
};

export default AddSingleContactModal;
